import { useCallback, useMemo, useState } from 'react'
import { API } from '../../services/api'
import { useDebouncedOptions } from '../../utils/useDebouncedOptions'

interface UseTagsInputConfig {
  workspaceId: string
  initialTags?: Array<string>
  onChange?: (tags: Array<string>) => void
}

export const useTagsInput = ({ workspaceId, initialTags, onChange }: UseTagsInputConfig) => {
  const [tags, setTags] = useState<Array<string>>(initialTags ?? [])

  const search = useCallback(
    async (phrase: string): Promise<Array<string>> => {
      const res = await API.getInstance().getTags(workspaceId, phrase, 5)
      return res.ok && res.data ? res.data.map((tag) => tag.name) : []
    },
    [workspaceId]
  )

  const { query, setQuery, options, loading } = useDebouncedOptions<string>({ search })

  const suggestions = useMemo(() => {
    const selected = new Set(tags.map((tag) => tag.toLowerCase()))
    return options.filter((option) => !selected.has(option.toLowerCase()))
  }, [options, tags])

  const commit = useCallback(
    (next: Array<string>) => {
      setTags(next)
      onChange?.(next)
    },
    [onChange]
  )

  const addTag = useCallback(
    (raw: string) => {
      const tag = raw.trim()
      setQuery('')
      if (!tag) return
      if (tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return
      commit([...tags, tag])
    },
    [tags, commit, setQuery]
  )

  const removeTag = useCallback(
    (tag: string) => {
      commit(tags.filter((existing) => existing !== tag))
    },
    [tags, commit]
  )

  return { tags, query, setQuery, suggestions, loading, addTag, removeTag }
}
